import { AfterViewInit, Component, Inject, inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormControl, FormGroup, UntypedFormGroup, Validators } from '@angular/forms';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AccountGroup } from '@galaxy/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { getLocale } from '@vendasta/galaxy/utility/locale';
import { DurationInterface, SalesOrdersService, Status } from '@vendasta/sales-orders';
import { BehaviorSubject, lastValueFrom, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, scan, take } from 'rxjs/operators';
import { ORDER_BUSINESS_CONFIG_TOKEN } from '../../core/tokens';
import { MARKET_ID, PARTNER_ID } from '../../shared/constants';
import { LineItemsService } from '../line-items/line-items.service';
import { DEFAULT_CONTRACT_DURATION } from '../order-details/contract-details/consts';
import { CreateOrderDetailsForm } from './create-order-details.interface';
import { OrderStoreService } from '../../core/orders.service';

@Component({
  selector: 'orders-create-order-details',
  templateUrl: './create-order-details.component.html',
  styleUrls: ['./create-order-details.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: getLocale() },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
  ],
  standalone: false,
})
export class CreateOrderDetailsComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly minDate = new Date(new Date().setHours(7, 0, 0, 0));
  private readonly orderBusinessConfig = inject(ORDER_BUSINESS_CONFIG_TOKEN, { optional: true });

  @Input() business: Pick<AccountGroup, 'napData' | 'accountGroupId'>;
  @Input() orderId: string;
  @Input() contractStartDate = this.minDate;
  @Input() formGroup?: UntypedFormGroup;
  @Input() createMode = false;
  @Input() viewOnly = false;

  _contractDuration: DurationInterface = DEFAULT_CONTRACT_DURATION;
  get contractDuration(): DurationInterface {
    return this._contractDuration;
  }

  @Input() set contractDuration(value: DurationInterface | null) {
    this._contractDuration = value ?? DEFAULT_CONTRACT_DURATION;
  }

  readonly contractDurationValueControl = new FormControl(this.contractDuration.value, [
    Validators.required,
    Validators.min(1),
  ]);
  readonly contractStartControl = new FormControl(this.contractStartDate, [Validators.required]);
  readonly contractDurationGroup = new FormGroup({
    value: this.contractDurationValueControl,
    duration: new FormControl(this.contractDuration.duration, [Validators.required]),
  });

  readonly headerForm = new FormGroup({
    contractStartDate: this.contractStartControl,
    contractDuration: this.contractDurationGroup,
  });

  private readonly updating$$ = new BehaviorSubject<number>(0);
  public readonly isUpdating$ = this.updating$$.pipe(
    scan((acc, value) => acc + value),
    map((total) => total > 0),
  );
  private readonly addUpdateOp = () => this.updating$$.next(1);
  private readonly completeUpdateOp = () => this.updating$$.next(-1);

  private readonly subscriptions = new Subscription();

  link$: Promise<string>;

  constructor(
    @Inject(PARTNER_ID) private readonly partnerId$: Observable<string>,
    @Inject(MARKET_ID) private readonly marketId$: Observable<string>,
    private readonly salesOrdersService: SalesOrdersService,
    private readonly lineItemsService: LineItemsService,
    private readonly snack: SnackbarService,
    @Optional() private readonly orderStoreService: OrderStoreService,
  ) {}

  ngOnInit(): void {
    this.initHeaderForm();
    this.link$ = this.getAccountLink$(this.business?.accountGroupId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewInit(): void {
    if (this.orderId && this.orderStoreService) {
      this.orderStoreService.order$.pipe(take(1)).subscribe((order) => {
        if (order.status === Status.DRAFTED) {
          this.validateStartDate();
        }
      });
    }
  }

  private initHeaderForm(): void {
    this.formGroup?.addControl('headerForm', this.headerForm);
    const durationControl = this.headerForm.controls.contractDuration;

    this.contractStartControl.setValue(this.contractStartDate);
    this.setLineItemsServiceContractStartDate(this.contractStartDate);

    if (this.contractDuration) {
      durationControl.setValue({
        value: this.contractDuration.value,
        duration: this.contractDuration.duration,
      });
      this.setLineItemsServiceContractDuration(this.contractDuration);
    }

    const dueTime = 250;
    this.subscriptions.add(
      durationControl.valueChanges
        .pipe(
          filter(() => durationControl.valid),
          debounceTime(dueTime),
        )
        .subscribe(() => {
          this.updateContractDuration();
        }),
    );

    this.subscriptions.add(
      this.contractStartControl.valueChanges
        .pipe(
          filter(() => this.contractStartControl.valid),
          debounceTime(dueTime),
        )
        .subscribe(() => {
          this.updateContractStartDate();
        }),
    );
  }

  handleContractStartDateChange(date: Date): void {
    this.contractStartControl.setValue(date);
    this.contractStartDate = date;
  }

  handleContractDurationChange(duration: DurationInterface): void {
    this.headerForm.controls.contractDuration.setValue({
      value: duration.value,
      duration: duration.duration,
    });
  }

  validateStartDate(): void {
    const today = new Date(new Date().setHours(7, 0, 0, 0));
    if (this.contractStartDate < today) {
      this.handleContractStartDateChange(today);
    }
  }

  // used for editing existing order details (updates contract start date value specifically)
  private updateContractStartDate(): void {
    const startDate = this.contractStartControl.value;
    if (this.orderId) {
      this.addUpdateOp();
      startDate.setHours(7);
      lastValueFrom(
        this.salesOrdersService.updateRequestedActivation(this.orderId, this.business.accountGroupId, startDate),
      )
        .then(() => this.orderStoreService.reloadOrder())
        .catch(() => this.snack.openErrorSnack('LIB_ORDERS.COMMON.ORDER_DETAILS.CONTRACT_START_UPDATE_ERROR'))
        .finally(this.completeUpdateOp);
    }

    this.setLineItemsServiceContractStartDate(startDate);
  }

  private updateContractDuration(): void {
    const contractDuration = this.headerForm.value.contractDuration;
    if (this.orderId) {
      this.addUpdateOp();
      lastValueFrom(
        this.salesOrdersService.updateContractDuration(this.orderId, this.business.accountGroupId, contractDuration),
      )
        .catch(() => this.snack.openErrorSnack('LIB_ORDERS.COMMON.ORDER_DETAILS.CONTRACT_DURATION_UPDATE_ERROR'))
        .finally(this.completeUpdateOp);
    }

    this.setLineItemsServiceContractDuration(contractDuration);
  }

  public getFormData(): CreateOrderDetailsForm {
    const data = this.headerForm.value;
    data.contractStartDate.setHours(7);
    return data as CreateOrderDetailsForm;
  }

  private getAccountLink$(accountGroupId: string): Promise<string> {
    const url = this.orderBusinessConfig?.getBusinessUrl?.(accountGroupId) ?? `/info/${accountGroupId}`;
    return this.makePromise(url);
  }

  private makePromise<T>(value: T | Promise<T>): Promise<T> {
    if (value instanceof Promise) {
      return value;
    }
    return Promise.resolve(value);
  }

  private setLineItemsServiceContractStartDate(startDate: Date): void {
    this.lineItemsService.setContractStartDate(startDate);
  }

  private setLineItemsServiceContractDuration(duration: DurationInterface): void {
    this.lineItemsService.setContractDuration(duration);
  }
}
